import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getSession } from "next-auth/react";

export const connectCalendar = createAsyncThunk(
  "calendarIntegration/connect",
  async ({ providerId, userId }, { rejectWithValue }) => {
    try {
      const session = await getSession();

      if (!session?.user?.sub) {
        throw new Error("No session found");
      }

      const response = await fetch(`/api/calendar/connect/${providerId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.error || "Failed to connect calendar");
      }

      const data = await response.json();

      if (data.authUrl) {
        window.location.href = data.authUrl;
        return { providerId, redirecting: true, authUrl: data.authUrl };
      }

      return { providerId, ...data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const disconnectCalendar = createAsyncThunk(
  "calendarIntegration/disconnect",
  async ({ providerId, userId }, { rejectWithValue }) => {
    try {
      const session = await getSession();

      if (!session?.user?.sub) {
        throw new Error("No session found");
      }

      const response = await fetch(`/api/calendar/disconnect`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ provider: providerId, userId }),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.error || "Failed to disconnect calendar");
      }

      const data = await response.json();
      return { providerId, ...data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const syncCalendarData = createAsyncThunk(
  "calendarIntegration/sync",
  async ({ userId, force = false }, { rejectWithValue, dispatch }) => {
    try {
      const session = await getSession();

      if (!session?.user?.sub) {
        throw new Error("No session found");
      }

      const response = await fetch("/api/calendar/sync", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId, force }),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.error || "Failed to sync calendar");
      }

      const syncData = await response.json();

      const availabilityResponse = await fetch(
        "/api/availability/get-availability",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session.accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (availabilityResponse.ok) {
        const availabilityData = await availabilityResponse.json();

        return {
          events: syncData.events || [],
          blockedPeriods: availabilityData.blocked_periods || [],
          syncedAt: syncData.syncedAt || new Date().toISOString(),
          summary: syncData.summary,
        };
      }

      return {
        events: syncData.events || [],
        blockedPeriods: syncData.blockedPeriods || [],
        syncedAt: syncData.syncedAt || new Date().toISOString(),
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const checkCalendarStatus = createAsyncThunk(
  "calendarIntegration/checkStatus",
  async (userId, { rejectWithValue }) => {
    try {
      const session = await getSession();

      if (!session?.user?.sub) {
        throw new Error("No session found");
      }

      const response = await fetch("/api/calendar/status", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(
          error.error || "Failed to check calendar status",
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchCalendarEvents = createAsyncThunk(
  "calendarIntegration/fetchEvents",
  async ({ userId, startDate, endDate }, { rejectWithValue }) => {
    try {
      const session = await getSession();

      if (!session?.user?.sub) {
        throw new Error("No session found");
      }

      const response = await fetch("/api/calendar/fetch", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId, startDate, endDate }),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(
          error.error || "Failed to fetch calendar events",
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const initialState = {
  connectedProviders: [],
  connectionStatus: "idle",
  connectionError: null,
  hasCheckedInitialStatus: false,

  syncInProgress: false,
  syncError: null,
  lastSyncTime: null,
  autoSyncEnabled: true,

  calendarEvents: [],
  autoBlockedPeriods: [],

  isLoading: false,
  error: null,
};

const calendarIntegrationSlice = createSlice({
  name: "calendarIntegration",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.connectionError = null;
      state.syncError = null;
    },

    setAutoSync: (state, action) => {
      state.autoSyncEnabled = action.payload;
    },

    setConnectedProviders: (state, action) => {
      state.connectedProviders = action.payload;
      state.hasCheckedInitialStatus = true;
    },

    addConnectedProvider: (state, action) => {
      const provider = action.payload;
      const exists = (state.connectedProviders || []).find(
        (p) => p.provider === provider.provider || p === provider.provider,
      );
      if (!exists) {
        state.connectedProviders.push(provider);
      }
    },

    removeConnectedProvider: (state, action) => {
      const providerId = action.payload;
      state.connectedProviders = state.connectedProviders.filter(
        (p) => p.provider !== providerId && p !== providerId,
      );
    },

    updateAutoBlockedPeriods: (state, action) => {
      state.autoBlockedPeriods = action.payload;
    },

    clearCalendarData: (state) => {
      state.calendarEvents = [];
      state.autoBlockedPeriods = [];
      state.lastSyncTime = null;
    },

    resetCalendarIntegration: (state) => {
      return { ...initialState };
    },

    handleOAuthCallback: (state, action) => {
      const { provider, email, connected } = action.payload;

      if (connected) {
        const providerData = {
          provider: provider,
          email: email,
          id: "primary",
          connected: true,
          connectedAt: new Date().toISOString(),
        };

        const exists = (state.connectedProviders || []).find(
          (p) => p.provider === provider,
        );
        if (!exists) {
          state.connectedProviders.push(providerData);
        }

        state.connectionStatus = "connected";
        state.connectionError = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(connectCalendar.pending, (state) => {
        state.connectionStatus = "connecting";
        state.connectionError = null;
        state.isLoading = true;
      })
      .addCase(connectCalendar.fulfilled, (state, action) => {
        state.isLoading = false;

        if (action.payload.redirecting) {
          state.connectionStatus = "redirecting";
          return;
        }

        state.connectionStatus = "connected";

        if (!action.payload.authUrl && !action.payload.redirecting) {
          const provider = {
            provider: action.payload.providerId,
            email: action.payload.email,
            id: action.payload.calendarId || "primary",
            connected: true,
            connectedAt: new Date().toISOString(),
          };

          const exists = (state.connectedProviders || []).find(
            (p) => p.provider === action.payload.providerId,
          );
          if (!exists) {
            state.connectedProviders.push(provider);
          }
        }
      })
      .addCase(connectCalendar.rejected, (state, action) => {
        state.connectionStatus = "error";
        state.connectionError = action.payload;
        state.isLoading = false;
      });

    builder
      .addCase(disconnectCalendar.pending, (state) => {
        state.connectionStatus = "disconnecting";
        state.connectionError = null;
        state.isLoading = true;
      })
      .addCase(disconnectCalendar.fulfilled, (state, action) => {
        state.connectionStatus = "idle";
        state.isLoading = false;

        const providerId = action.payload.providerId || action.payload.provider;
        state.connectedProviders = state.connectedProviders.filter(
          (p) => p.provider !== providerId && p !== providerId,
        );

        state.autoBlockedPeriods = state.autoBlockedPeriods.filter(
          (period) => period.provider !== providerId,
        );

        if (state.connectedProviders.length === 0) {
          state.calendarEvents = [];
          state.autoBlockedPeriods = [];
          state.lastSyncTime = null;
        }
      })
      .addCase(disconnectCalendar.rejected, (state, action) => {
        state.connectionStatus = "error";
        state.connectionError = action.payload;
        state.isLoading = false;
      });

    builder
      .addCase(syncCalendarData.pending, (state) => {
        state.syncInProgress = true;
        state.syncError = null;
      })
      .addCase(syncCalendarData.fulfilled, (state, action) => {
        state.syncInProgress = false;
        state.lastSyncTime = action.payload.syncedAt;
        state.calendarEvents = action.payload.events || [];
        state.autoBlockedPeriods = [...(action.payload.blockedPeriods || [])];

        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("calendarSynced"));
        }
      })
      .addCase(syncCalendarData.rejected, (state, action) => {
        state.syncInProgress = false;
        state.syncError = action.payload;
      });

    builder
      .addCase(checkCalendarStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkCalendarStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.connectedProviders = action.payload.connectedCalendars || [];
        state.hasCheckedInitialStatus = true;
      })
      .addCase(checkCalendarStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.hasCheckedInitialStatus = true;
      });

    builder
      .addCase(fetchCalendarEvents.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCalendarEvents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.calendarEvents = action.payload.events || [];

        if (action.payload.blockedPeriods) {
          state.autoBlockedPeriods = action.payload.blockedPeriods;
        }
      })
      .addCase(fetchCalendarEvents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setAutoSync,
  setConnectedProviders,
  addConnectedProvider,
  removeConnectedProvider,
  updateAutoBlockedPeriods,
  clearCalendarData,
  resetCalendarIntegration,
  handleOAuthCallback,
} = calendarIntegrationSlice.actions;

export default calendarIntegrationSlice.reducer;
